import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";
import { cn } from "@/lib/utils";

export interface UnifiedCardProps {
  icon?: LucideIcon;
  title?: string;
  children: ReactNode;
  className?: string;
  noDivider?: boolean;
}

/**
 * Single reusable card wrapper for the teacher analytics redesign — one consistent
 * opaque look (border/radius/shadow) everywhere, replacing the old page's three
 * different pastel/backdrop-blur treatments (indigo-50/80, sky-50/80, emerald-50/40).
 *
 * Always fully opaque (`bg-card`) — never translucent/backdrop-blur — because
 * `.teacher-theme main` renders an opaque, `!important` dot-grid background behind
 * every card on this page.
 */
export function UnifiedCard({ icon: Icon, title, children, className, noDivider }: UnifiedCardProps) {
  const hasHeader = Boolean(Icon || title);

  return (
    <div className={cn("rounded-2xl border border-border bg-card shadow-sm overflow-hidden", className)}>
      {hasHeader && (
        <div className={cn("flex items-center gap-2 px-5 py-4", !noDivider && "border-b border-border")}>
          {Icon && <Icon className="w-4 h-4 text-primary" aria-hidden="true" />}
          {title && <h2 className="font-display text-base font-semibold text-foreground">{title}</h2>}
        </div>
      )}
      <div className="p-5">{children}</div>
    </div>
  );
}

export default UnifiedCard;
